// Import necessary functions from Pinia and Nuxt.js Composition API
import { defineStore } from 'pinia';
import { computed, ref } from '@nuxtjs/composition-api';

/**
 * Define a store managing the flow of steps in a process.
 */
export const useFlowStore = defineStore('flowStore', () => {
  // Initialize reactive variables
  const steps = ref([]); // Holds the steps of the flow
  const panelModel = ref(0); // Model for the panel, likely used for UI state
  const currentStep = ref(0); // Current step index in the flow
  const flowStatus = ref(''); // Status of the flow, e.g., "completed", "in-progress"

  /**
   * Computed property to get the current steps of the flow.
   * @returns {Array} The current steps.
   */
  const getSteps = computed(() => steps);

  /**
   * Computed property to get the current panel model.
   * @returns {number} The current panel model.
   */
  const getPanelModel = computed(() => panelModel);

  /**
   * Computed property to get the current step index.
   * @returns {number} The current step index.
   */
  const getCurrentStep = computed(() => currentStep);

  /**
   * Computed property to get the current flow status.
   * @returns {string} The current flow status.
   */
  const getFlowStatus = computed(() => flowStatus);

  /**
   * Computed property to get the status of the current step.
   * @returns {string} The status of the current step.
   */
  const getCurrentStepStatus = computed(() => getStepStatus(currentStep.value));

  /**
   * Function to get the status of a specific step by index.
   * @param {number} index - The index of the step.
   * @returns {string} The status of the step at the given index.
   */
  const getStepStatus = (index) => steps.value?.at(index)?.status;

  /**
   * Function to set the flow status.
   * @param {string} status - The new status for the flow.
   * @returns {string} The updated flow status.
   */
  const setStatus = (status) => {
    flowStatus.value = status;
  };

  /**
   * Function to reset the store to its initial state.
   */
  const reset = () => {
    steps.value = [];
    currentStep.value = 0;
    panelModel.value = 0;
  };

  /**
   * Function to add a new step to the flow.
   * @param {Object} step - The new step to add.
   */
  const addStep = (step) => {
    steps.value.push(step);
  };

  /**
   * Function to set the status of a specific step.
   * @param {number} index - The index of the step.
   * @param {string} flowStatus - The new status for the step.
   */
  const setStepStatus = (index, flowStatus) => {
    steps.value.at(index).status = flowStatus;
  };

  /**
   * Function to increase the current step index.
   */
  const increaseCurrentStep = () => {
    currentStep.value = currentStep.value + 1;
    setPanelModel(currentStep.value)
  };

  /**
   * Function to set the current step index.
   * @param {number} index - The new current step index.
   */
  const setCurrentStep = index => {
    currentStep.value = index;
    setPanelModel(index)
  };

  /**
   * Function to set the panel model based on the provided value.
   */
  const setPanelModel = (value) => {
    panelModel.value = value
  };

  /**
   * Function to update the panel model based on the current step.
   */
  const updatePanelModel = () => {
    panelModel.value = currentStep.value;
  };

  return {
    steps,
    panelModel,
    currentStep,
    flowStatus,
    getSteps,
    getPanelModel,
    getCurrentStep,
    getFlowStatus,
    getCurrentStepStatus,
    reset,
    setStatus,
    addStep,
    setStepStatus,
    increaseCurrentStep,
    setCurrentStep,
    setPanelModel,
    getStepStatus,
    updatePanelModel
  };
});
